/**
 * MER-470: latest-populated-period fallback window.
 *
 * When a period-mode table widget's selected window has zero rows but the
 * source carries rows in an earlier window (feed lag: the DWS SE feed's
 * newest event was June 1 while "this week" pointed at June 8-14), the
 * renderer falls back to the latest populated period and LABELS it --
 * never a bare "No data for this period" while the source is non-empty,
 * and never silently presenting old data as current.
 *
 * Pure date math, no Supabase dependency, .mjs so the node:test suite can
 * import it directly (the tenantScope.mjs precedent).
 *
 * @param {string | undefined} range   manifest timeline range (this_week,
 *   this_month, last_month, last_30_days, ...)
 * @param {string} maxIso   newest timeline-column value in the source
 *   (ISO date or timestamp; only the date part is used)
 * @param {number | undefined} trailingDays   resolved TIMELINE_DAY_RANGES
 *   entry for trailing-window ranges; undefined for calendar ranges
 * @returns {{ gte: string, lt: string, label: string } | null}
 */
export function computeFallbackWindow(range, maxIso, trailingDays) {
  const datePart = String(maxIso).slice(0, 10);
  const d = new Date(datePart + "T00:00:00Z");
  if (!Number.isFinite(d.getTime())) return null;
  const iso = (x) => x.toISOString().slice(0, 10);

  if (range === "this_week" || range === "last_week") {
    const monday = new Date(d);
    monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
    const next = new Date(monday);
    next.setUTCDate(next.getUTCDate() + 7);
    return { gte: iso(monday), lt: iso(next), label: `week of ${iso(monday)}` };
  }
  if (range === "this_month" || range === "last_month") {
    const start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
    const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
    return { gte: iso(start), lt: iso(next), label: iso(start).slice(0, 7) };
  }
  if (trailingDays && trailingDays > 0) {
    const start = new Date(d);
    start.setUTCDate(start.getUTCDate() - trailingDays);
    const next = new Date(d);
    next.setUTCDate(next.getUTCDate() + 1);
    return {
      gte: iso(start),
      lt: iso(next),
      label: `${trailingDays} days ending ${iso(d)}`,
    };
  }
  return null;
}
